import React from 'react';
import { type MissionSettings } from '../../api/types.js';
import { InputField, SelectField, TextAreaField } from './Field.js';
import SaveBar from './SaveBar.js';
import { useEditable } from './useEditable.js';
import ToggleRow from './ToggleRow.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface Props {
  value: MissionSettings;
  onSave: (next: MissionSettings) => Promise<void>;
}

function MissionTab({ value, onSave }: Props) {
  const { t } = useTranslation();
  const { draft, set, dirty, saving, reset, save } = useEditable(value, onSave);

  const MISSION_STATUS = [
    { value: 'active', label: t('mission.statusActive') },
    { value: 'standby', label: t('mission.statusStandby') },
    { value: 'archived', label: t('mission.statusArchived') },
  ];

  const CLASSIFICATION = [
    { value: 'restricted', label: t('mission.classRestricted') },
    { value: 'confidential', label: t('mission.classConfidential') },
    { value: 'secret', label: t('mission.classSecret') },
  ];

  return (
    <div className="sp on">
      <div className="sp-title">{t('mission.title')}</div>
      <div className="sp-subtitle">{t('mission.subtitle')}</div>

      <div className="sg">
        <div className="sg-title">{t('mission.identity')}</div>
        <div className="sg-grid one">
          <InputField label={t('mission.name')} value={draft.name} onChange={(v) => set('name', v)} />
          <TextAreaField
            label={t('mission.description')}
            value={draft.description}
            rows={3}
            onChange={(v) => set('description', v)}
          />
        </div>
        <div className="sg-grid">
          <InputField
            label={t('mission.startDate')}
            type="date"
            value={draft.startDate}
            onChange={(v) => set('startDate', v)}
          />
          <InputField
            label={t('mission.endDate')}
            type="date"
            value={draft.endDate}
            onChange={(v) => set('endDate', v)}
          />
          <SelectField
            label={t('mission.status')}
            value={draft.status}
            options={MISSION_STATUS}
            onChange={(v) => set('status', v)}
          />
          <SelectField
            label={t('mission.classificationLevel')}
            value={draft.classificationLevel}
            options={CLASSIFICATION}
            onChange={(v) => set('classificationLevel', v)}
          />
        </div>
      </div>

      <div className="sg">
        <div className="sg-title">{t('mission.geoScope')}</div>
        <div className="sg-grid">
          <InputField
            label={t('mission.primaryCity')}
            value={draft.primaryCity}
            onChange={(v) => set('primaryCity', v)}
          />
          <InputField
            label={t('mission.priorityDistricts')}
            value={draft.priorityDistricts}
            onChange={(v) => set('priorityDistricts', v)}
          />
          <InputField
            label={t('mission.mapCenterLat')}
            type="number"
            value={draft.mapCenterLat}
            onChange={(v) => set('mapCenterLat', Number(v))}
          />
          <InputField
            label={t('mission.mapCenterLng')}
            type="number"
            value={draft.mapCenterLng}
            onChange={(v) => set('mapCenterLng', Number(v))}
          />
          <ToggleRow
            label={t('mission.autoCenterOnSites')}
            checked={draft.autoCenterOnSites}
            onChange={(v) => set('autoCenterOnSites', v)}
          />
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onCancel={reset} onSave={save} />
    </div>
  );
}

export default MissionTab;
